// Total do grid em requisição à parte. A contagem de um filtro que casa com centenas de milhares de registros leva
// ~11 s e a página, ~1 s; pedidas juntas, a página esperava a contagem. Agora a lista sai sem o total
// (`includeTotal: false`) e o total vem de `POST /talk/filter/{subject}/count`, em paralelo.
//
// Regras (o paginador nunca fica sem resposta e nunca mostra um total velho):
//  - Só TROCA DE PÁGINA / DE TAMANHO DE PÁGINA / ORDEM reaproveitam a contagem (o filtro é o mesmo). Atualizar o grid,
//    mudar o filtro, o status ou salvar/excluir um registro contam de novo.
//  - Reaproveitar vale também para uma contagem ainda em curso: paginar enquanto ela roda não a cancela nem a repete.
//  - Contagem obsoleta (outro filtro, outra tela) é cancelada: a resposta tardia nunca sobrescreve a vigente.
//  - Página que volta incompleta já revela o total exato (é a última); nesse caso a contagem em curso é cancelada.
//  - Enquanto a contagem não chega, o paginador recebe um total provisório (uma página a mais, para o "próxima" funcionar)
//    e mostra "Calculando total…" em vez do número.

// Campos do corpo da lista que NÃO mudam o conjunto de registros: paginação, ordem e o que o servidor ignora.
const NOT_PART_OF_THE_COUNT = ['page', 'fetch', 'order', 'includeTotal', 'scenario', 'talkParent']

export const buildCountBody = (params) => {
  const body = {}
  Object.keys(params || {}).forEach((name) => {
    if (!NOT_PART_OF_THE_COUNT.includes(name) && params[name] !== undefined) body[name] = params[name]
  })
  return body
}

// Mesmo subject + mesmo corpo da contagem = mesmo conjunto de registros.
export const buildTotalKey = (subject, body) =>
  JSON.stringify([String(subject).toLowerCase(), JSON.stringify(body, Object.keys(body).sort())])

const isCanceled = (error) =>
  error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError'

// O que o paginador deve mostrar para um estado de contagem. `page`/`perPage` só servem ao provisório e à falha.
export const describeTotal = (state, { page, perPage }) => {
  if (!state.done) return { total: page * perPage + 1, capped: false, loading: true }
  if (state.failed) return { total: page * perPage, capped: true, loading: false } // só se sabe que há "mais de" o que já foi mostrado
  return { total: state.total, capped: state.capped, loading: false }
}

export class GridTotal {
  // post(url, body, config) devolve uma promessa de { data: { totalResults, totalIsCapped } } (axios).
  constructor ({ post }) {
    this.post = post
    this.state = null
  }

  // Inicia a contagem (ou reaproveita a que já existe). `onChange(state)` roda quando a contagem em curso termina.
  start ({ url, key, body, reuse, onChange }) {
    if (reuse && this.state && this.state.key === key && !this.state.failed) return this.state

    this.cancel()

    const controller = new AbortController()
    const state = { key, controller, done: false, failed: false, total: 0, capped: false }
    this.state = state

    this.post(url, body, { signal: controller.signal })
      .then(({ data }) => {
        if (this.state !== state || state.done) return
        state.total = data?.totalResults || 0
        state.capped = !!data?.totalIsCapped
        state.done = true
        onChange(state)
      })
      .catch((error) => {
        if (this.state !== state || state.done || isCanceled(error)) return
        state.failed = true
        state.done = true
        onChange(state)
      })

    return state
  }

  // O servidor já devolveu o total na própria lista (Subject de tabela física): vale ele, e a contagem em curso é dispensada.
  settle (state, total, capped = false) {
    if (this.state !== state) return false
    state.total = total
    state.capped = capped
    state.failed = false
    state.done = true
    state.controller.abort()
    return true
  }

  // Página incompleta = última página: o total é exato e dispensa a contagem. Página cheia: continua esperando a contagem.
  settleFromRows (state, { page, perPage, rows }) {
    if (this.state !== state || state.done) return false
    if (rows >= perPage) return false
    if (rows === 0 && page > 1) return false // página além do fim (registros removidos): o total exato só a contagem sabe
    return this.settle(state, (page - 1) * perPage + rows)
  }

  cancel () {
    if (this.state && !this.state.done) this.state.controller.abort()
    this.state = null
  }
}
